import { FunctionComponent } from 'react';
import {
    ALL_PRODUCT_ENVIRONMENTS,
    ProductConfiguration,
    PriceCondition,
} from '@/models/product';
import { v4 as uuid } from 'uuid';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import { usePostOrPutConfigurationMutation } from '@/api/v2/productConfigurations';
import { PriceConditionForm } from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/PriceConditionForm';

const buildInitialPriceCondition = (
    pC: ProductConfiguration
): PriceCondition => ({
    id: uuid(),
    productConfigurationId: pC.id,
    name: '',
    externalName: {
        de: '',
        en: '',
    },
    sort: undefined,
    minDaysBeforeTicketStart: undefined,

    validFrom: null,
    validTo: null,
    startDateValidFrom: null,
    startDateValidTo: null,
    validWeekdays: new Set<string>(),
    startDateValidWeekdays: new Set<string>(),
    startDateContingentFrom: null,
    startDateContingentTo: null,
    conditionContingentFrom: null,
    conditionContingentTo: null,
    validityPeriodContingentFrom: null,
    validityPeriodContingentTo: null,
    personTypeIds: [],
    sellingGroup: null,
    notSellingGroup: null,
    priceChange: { amount: 0, currency: 'EUR' },
    priceChangeAbsolute: false,
    blockCancellation: false,
    dayContingentFrom: null,
    dayContingentTo: null,
    roundTo: null,
    round50: false,
    environments: ALL_PRODUCT_ENVIRONMENTS,
});

type PriceConditionFormContainerProps = {
    onClose: () => void;
    productConfiguration: ProductConfiguration;
    condition?: PriceCondition;
};

export const PriceConditionFormContainer: FunctionComponent<
    React.PropsWithChildren<PriceConditionFormContainerProps>
> = ({ condition, productConfiguration, onClose }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { addNotification } = useNotification();
    const [apiAction, { isLoading }] = usePostOrPutConfigurationMutation({
        liftOperatorId,
        productConfiguration,
    });

    const isNewCondition = !condition?.id;

    // save or update logic
    const saveOrUpdate = (nextCondition: PriceCondition) => {
        const nextConfiguration = {
            ...productConfiguration,
        };
        if (!isNewCondition) {
            const conditionIndex =
                productConfiguration.priceConditions.findIndex(
                    (c) => condition.id === c.id
                );

            if (conditionIndex > -1) {
                const priceConditions = [
                    ...productConfiguration.priceConditions,
                ];

                priceConditions.splice(conditionIndex, 1, nextCondition);

                nextConfiguration.priceConditions = priceConditions;
            }
        } else {
            const highestSort = Math.max(
                ...nextConfiguration.priceConditions.map((p) => p.sort)
            );
            nextConfiguration.priceConditions = [
                ...nextConfiguration.priceConditions,
                {
                    ...nextCondition,
                    sort: highestSort + 1,
                },
            ];
        }

        apiAction({ liftOperatorId, productConfiguration: nextConfiguration })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Regel gespeichert',
                    type: 'success',
                });
                onClose();
            })
            .catch(() => {
                addNotification({
                    message: 'Fehler beim speichern',
                    type: 'error',
                });
            });
    };

    return (
        <PriceConditionForm
            condition={
                condition || buildInitialPriceCondition(productConfiguration)
            }
            isNewCondition={isNewCondition}
            onSave={saveOrUpdate}
            onClose={onClose}
            isLoading={isLoading}
        />
    );
};
